import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Undo2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

/** Confirm putting one or many closed lanes back with their desks. items: [{lead_id, lender_id, case, desk}] */
export function RestoreDialog({ open, onOpenChange, items, onDone, testId = "hub-restore-dialog" }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setNote(""); }, [open]);
  const desks = [...new Set(items.map((x) => x.desk))];
  const one = items.length === 1;

  const go = async () => {
    setBusy(true);
    try {
      const body = { items: items.map(({ lead_id, lender_id }) => ({ lead_id, lender_id })), note: note.trim() || null };
      const { data } = await api.post("/hub/lanes/restore", body);
      const n = data.restored.length;
      if (n) toast.success(`${n} case${n === 1 ? "" : "s"} back with ${data.desks_told} desk${data.desks_told === 1 ? "" : "s"}`,
        { description: "Each desk got one email listing what came back." });
      if (data.failed.length) toast.error(`${data.failed.length} couldn't go back`, { description: data.failed[0].error });
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "That didn't go through"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="sm:max-w-md" data-testid={testId}>
        <DialogHeader>
          <DialogTitle className="text-[17px]">
            {one ? `Put ${items[0]?.case} back with ${items[0]?.desk}` : `Put ${items.length} cases back`}
          </DialogTitle>
          <DialogDescription className="text-[13px]">
            {one ? "Their lane re-opens" : `${desks.length} desk${desks.length === 1 ? "" : "s"}: lanes re-open`} where they were, the client&apos;s
            paperwork returns to their portal, the case thread opens again and the SLA clock starts fresh. Each desk gets one email.
          </DialogDescription>
        </DialogHeader>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="A line for the desk (optional) — it goes in the same email."
                  className="min-h-[70px] resize-none" data-testid={`${testId}-note`} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="rounded-full" data-testid={`${testId}-cancel`}>Cancel</Button>
          <Button onClick={go} disabled={busy || !items.length} className="rounded-full" data-testid={`${testId}-confirm`}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <><Undo2 className="mr-1.5 size-3.5" /> Put back</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RestoreDialog;
